import * as React from 'react';
import { CheckStateContext } from '../../providers/CheckStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import SnackBar, { SnackBarProps, SnackBarType } from '../common/SnackBar';
import { String_WebHookNotification_AmountClaimed, String_WebHookNotification_ClaimedExpiredSelf, 
    String_WebHookNotification_ClaimedExpiredOthers, String_WebHookNotification_PaymentCompleted,
    String_WebHookNotification_CheckClosed, String_WebHookNotification_AmountClaimedRefreshCheck,
    String_WebHookNotification_PaymentCompletedRefreshCheck, 
    String_WebHookNotification_PaymentCompletedClaimExceedsAmountDue, 
    String_WebHookNotification_ClaimedExpiredOthersRefreshCheck, 
    String_WebHookNotification_CheckClosedRefreshCheck} from '../../utilities/StringHelper'
import { JsonHubProtocol, HubConnectionState, HubConnectionBuilder, LogLevel, IHttpConnectionOptions, HttpTransportType } from '@microsoft/signalr';
import { CheckSummary } from '../../models/common/CheckSummary';
import { useHistory } from 'react-router';

export enum CheckMessageType {
    None = 0,
    Subscribe = 1,
    Unsubscribe = 2,
}

export enum CheckNotificationType {
    AmountClaimed = 0,
    PaymentCompleted = 1,
    CheckClosed = 2,
    ClaimExpired = 3,
}

export type OnInitializedHandler = () => any;

export type OnNotificationReceivedHandler = (type: CheckNotificationType, amount: number) => any;

export interface SignalRProps {
    oldChecks: CheckSummary[] | null;
    messageType: CheckMessageType;
    load: boolean;
    refreshCheck: boolean;
    onNotificationReceived: OnNotificationReceivedHandler | null;
    onInitialized: OnInitializedHandler;
}

export interface AmountClaimedNotification {
    amount_claimed: number;
    amount_claimed_string: string;
    amount_due: number;
    amount_due_string: string;
    check_number: string;
    user_id: number;
}

export interface PaymentCompletedNotification {
    amount_paid: number;
    amount_paid_string: string;
    amount_due: number;
    amount_due_string: string;
    check_number: string;
    user_id: number;
}

export interface CheckClosedNotification {
    check_number: string;
    user_id: number;
}

export interface ClaimExpiredNotification {
    amount_claimed: number;
    amount_claimed_string: string;
    amount_due: number;
    amount_due_string: string;
    check_number: string;
    user_id: number;
}

const SignalR = (props: SignalRProps) => {
    const { oldChecks, messageType, load, refreshCheck, onNotificationReceived, onInitialized } = props;
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const checkState = React.useContext(CheckStateContext);
    const [notificationMessage, setNotificationMessage] = React.useState<string>('');
    const [initialized, setInitialized] = React.useState<boolean>(false);
    const [snackBarOpen, setSnackBarOpen] = React.useState<boolean>(false);
    const [connection, setConnection] = React.useState<any | null>(null);
    
    let history = useHistory();

    React.useEffect(() => {
        if (!initialized && load) {
            setupSignalRConnection();
            startSignalRConnection();
        }                
        return () => {
            try{
                if (connection !== null && initialized) {
                    unsubscribeFromCheckNotification();
                    connection.stop();
                }
            }
            catch (err) {

            }
        };
        //onInitialized();
    }, [initialized, load, connection]);

    const setupSignalRConnection = () => {

        if (connection === null) {
            try {
                setConnection(new HubConnectionBuilder()
                    .withUrl(process.env.REACT_APP_CHECKOUT_API_URL!!.toString() + '/checknotificationHub')
                    .withAutomaticReconnect()
                    .withHubProtocol(new JsonHubProtocol())
                    .configureLogging(LogLevel.Error)
                    .build());
            }
            catch (err) {
                console.error(err);
            } 
        }        
    
        if (connection !== null) {
            connection.serverTimeoutInMilliseconds = 60000;
    
            connection.onclose((error: any) => {
                console.assert(connection.state === HubConnectionState.Disconnected);
                console.log('SignalR connection closed due to error. Try refreshing this page to restart the connection', error);
            });
        
            connection.onreconnecting((error: any) => {
                console.assert(connection.state === HubConnectionState.Reconnecting);
                console.log('SignalR connection lost due to error. Reconnecting.', error);
            });
        
            connection.onreconnected((connectionId: any) => {
                if (messageType === CheckMessageType.Subscribe) {
                    if (oldChecks !== null) {
                        unsubscribeFromCheckNotification();
                    }

                    subscribeToCheckNotification();
                }
                else if (messageType === CheckMessageType.Unsubscribe) {
                    unsubscribeFromCheckNotification();
                }
    
                console.assert(connection.state === HubConnectionState.Connected);
                console.log('SignalR connection reestablished. Connected with connectionId', connectionId);
            });
        
            connection.on("AmountClaimedNotification", (notificationString: string) => {
                let notification: AmountClaimedNotification = JSON.parse(notificationString);

                if (notification.user_id !== userState.state.userId) {
                    if (refreshCheck) {
                        setNotificationMessage(String_WebHookNotification_AmountClaimedRefreshCheck(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    else {
                        setNotificationMessage(String_WebHookNotification_AmountClaimed(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    setSnackBarOpen(true);
                    if (onNotificationReceived !== null) {
                        onNotificationReceived(CheckNotificationType.AmountClaimed, notification.amount_due);
                    }
                }                
            });

            connection.on("PaymentCompletedNotification", (notificationString: string) => {
                let notification: PaymentCompletedNotification = JSON.parse(notificationString);

                if (notification.user_id !== userState.state.userId) {
                    if (refreshCheck) {
                        setNotificationMessage(String_WebHookNotification_PaymentCompletedRefreshCheck(notification.amount_paid_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    else {
                        setNotificationMessage(String_WebHookNotification_PaymentCompleted(notification.amount_paid_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    setSnackBarOpen(true);
                    if (onNotificationReceived !== null) {
                        onNotificationReceived(CheckNotificationType.PaymentCompleted, notification.amount_due);
                    }
                }
            });

            connection.on("CheckClosedNotification", (notificationString: string) => {
                let notification: CheckClosedNotification = JSON.parse(notificationString);
                
                if (notification.user_id !== userState.state.userId) {
                    if (refreshCheck) {
                        setNotificationMessage(String_WebHookNotification_CheckClosedRefreshCheck(notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    else {
                        setNotificationMessage(String_WebHookNotification_CheckClosed(notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    setSnackBarOpen(true);
                    if (onNotificationReceived !== null) {
                        onNotificationReceived(CheckNotificationType.CheckClosed, 0);
                    }
                }
            });

            connection.on("ClaimExpiredNotification", (notificationString: string) => {
                let notification: ClaimExpiredNotification = JSON.parse(notificationString);

                if (notification.user_id === userState.state.userId) {
                    setNotificationMessage(String_WebHookNotification_ClaimedExpiredSelf(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                }
                else {
                    if (refreshCheck) {
                        setNotificationMessage(String_WebHookNotification_ClaimedExpiredOthersRefreshCheck(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    else {
                        setNotificationMessage(String_WebHookNotification_ClaimedExpiredOthers(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                }

                setSnackBarOpen(true);
                if (onNotificationReceived !== null) {
                    onNotificationReceived(CheckNotificationType.ClaimExpired, notification.amount_due);
                }
            });

            connection.on("ClaimExpiredViewCheckScreenNotification", (notificationString: string) => {
                let notification: ClaimExpiredNotification = JSON.parse(notificationString);

                if (notification.user_id !== userState.state.userId) {
                    if (refreshCheck) {
                        setNotificationMessage(String_WebHookNotification_ClaimedExpiredOthersRefreshCheck(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }
                    else {
                        setNotificationMessage(String_WebHookNotification_ClaimedExpiredOthers(notification.amount_claimed_string, notification.check_number).find(s => s.key === userState.state.locale)!!.value);
                    }

                    setSnackBarOpen(true);

                    if (onNotificationReceived !== null) {
                        onNotificationReceived(CheckNotificationType.ClaimExpired, notification.amount_due);
                    }
                }
            });
        }        
    }

    const startSignalRConnection = async () => {
        try {
            if (connection !== null) {
                await connection.start();
                setInitialized(true);
    
                if (!initialized) {
                    if (messageType === CheckMessageType.Subscribe) {
                        if (oldChecks !== null) {
                            unsubscribeFromCheckNotification();    
                        }
                        
                        subscribeToCheckNotification();
                    }
                    else if (messageType === CheckMessageType.Unsubscribe) {
                        unsubscribeFromCheckNotification();
                    }
                    onInitialized();
                }               
                
                console.assert(connection.state === HubConnectionState.Connected);
                console.log('SignalR connection established');
            }            
        } 
        catch (err) {
            console.assert(connection.state === HubConnectionState.Disconnected);
            console.error('SignalR Connection Error: ', err);
            setTimeout(() => startSignalRConnection(), 5000);
        }
    };

    const subscribeToCheckNotification = async () => {
        try {
            if (checkOutState.state.checks !== null) {
                let checkIds: string[] = [];
                checkOutState.state.checks.forEach(check => {
                    checkIds.push(check.id);
                });
                await connection.invoke("SubscribeToCheckNotification", checkIds);
            }            
        } 
        catch (err) {
            console.error(err);
        }
    }
    
    const unsubscribeFromCheckNotification = async () => {
        try {
            if (oldChecks !== null) {
                let checkIds: string[] = [];
                oldChecks.forEach(check => {
                    checkIds.push(check.id);
                });
    
                await connection.invoke("UnsubscribeFromCheckNotification", checkIds);
            }
            else if (checkOutState.state.checks !== null) {
                let checkIds: string[] = [];
                checkOutState.state.checks.forEach(check => {
                    checkIds.push(check.id);
                });
    
                await connection.invoke("UnsubscribeFromCheckNotification", checkIds);
            }            
        } 
        catch (err) {
            console.error(err);
        }
    }

    const snackBarCloseHandler = () => {
        setSnackBarOpen(false);
    }

    const snackBarProps: SnackBarProps = {
        message: notificationMessage,
        type: SnackBarType.Information,
        open: snackBarOpen,
        onCloseHandler: snackBarCloseHandler
    }

    return (
        <div>
            <SnackBar 
                message={snackBarProps.message}
                type={snackBarProps.type}
                open={snackBarProps.open}
                onCloseHandler={snackBarProps.onCloseHandler} />
        </div>
    );
}

export default SignalR;